import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useGetHotelBidFailureLogs } from '~/hooks/apis/public-offer-feed/useGetHotelBidFailureLogs';
import {
  useDeleteHotelBidFailureLogs,
  useRetryHotelBidFailures,
} from '~/hooks/apis/public-offer-feed/useHotelBidFailureMutations';
import useQuery from '~/hooks/useQuery';

import { HotelBidFailureLog as HotelBidFailureLogRecord } from '~/services/PublicOfferFeedService';
import { endOfDay, startOfDay } from '~/services/TimeService';

import dateFormatter from '~/utils/dateFormatter';

const MAX_SELECTION = 500;

const COLUMNS: Array<GridColDef<HotelBidFailureLogRecord>> = [
  { field: 'id', headerName: 'ID', flex: 1.5 },
  { field: 'campaignId', headerName: 'Campaign ID', flex: 1 },
  { field: 'hotelId', headerName: 'Hotel ID', flex: 2.5 },
  { field: 'action', headerName: 'Action', flex: 1 },
  { field: 'bidTarget', headerName: 'Bid Target', flex: 1 },
  {
    field: 'userId',
    headerName: 'Changed By',
    flex: 1,
    renderCell: (params) => params.row.userId ?? 'Automation',
  },
  {
    field: 'errorDetails',
    headerName: 'Error Details',
    flex: 4,
    renderCell: (params) => {
      const json = JSON.stringify(params.row.errorDetails);
      const truncated = json.length > 80 ? `${json.slice(0, 80)}…` : json;
      return (
        <Tooltip title={json}>
          <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {truncated}
          </Box>
        </Tooltip>
      );
    },
  },
  {
    field: 'uploadedAt',
    headerName: 'Uploaded At',
    flex: 1.5,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => dateFormatter(params.row.uploadedAt),
  },
];

type ActionFilter = '' | 'CREATE' | 'UPDATE' | 'DELETE';

interface FilterFormValues {
  customerId: string;
  campaignId: string;
  hotelId: string;
  action: ActionFilter;
  from: string;
  to: string;
}

function HotelBidFailureLog() {
  const query = useQuery();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const [activeFilters, setActiveFilters] = useState<FilterFormValues>({
    customerId: query.get('customerId') || '',
    campaignId: query.get('campaignId') || '',
    hotelId: query.get('hotelId') || '',
    action: (query.get('action') as ActionFilter) || '',
    from: query.get('from') || '',
    to: query.get('to') || '',
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: Number(query.get('page_num') ?? 0),
    pageSize: Number(query.get('page_size') ?? 20),
  });

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [rowCount, setRowCount] = useState(0);
  const [retryDialogOpen, setRetryDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { register, handleSubmit, control } = useForm<FilterFormValues>({
    defaultValues: activeFilters,
  });

  const queryParams = {
    campaignId: activeFilters.campaignId || undefined,
    hotelId: activeFilters.hotelId || undefined,
    action: (activeFilters.action as HotelBidFailureLogRecord['action']) || undefined,
    from: activeFilters.from ? startOfDay(activeFilters.from).toISOString() : undefined,
    to: activeFilters.to ? endOfDay(activeFilters.to).toISOString() : undefined,
    page: paginationModel.page,
    limit: paginationModel.pageSize,
  };

  const { data, isFetching, isError, error } = useGetHotelBidFailureLogs(activeFilters.customerId || null, queryParams);

  // Stabilise rowCount so the DataGrid does not reset page while re-fetching
  useEffect(() => {
    if (data?.count !== undefined) {
      setRowCount(data.count);
    }
  }, [data?.count]);

  useEffect(() => {
    if (isError && error) {
      enqueueSnackbar(`Error fetching failure logs: ${error.message}`, { variant: 'error' });
    }
  }, [isError, error, enqueueSnackbar]);

  const retryMutation = useRetryHotelBidFailures();
  const deleteMutation = useDeleteHotelBidFailureLogs();

  const pushUrlParams = useCallback(
    (filters: FilterFormValues, page: number, pageSize: number) => {
      history.push({
        search: `?${qs.stringify(
          {
            customerId: filters.customerId || undefined,
            campaignId: filters.campaignId || undefined,
            hotelId: filters.hotelId || undefined,
            action: filters.action || undefined,
            from: filters.from || undefined,
            to: filters.to || undefined,
            page_num: page,
            page_size: pageSize,
          },
          { skipNulls: true },
        )}`,
      });
    },
    [history],
  );

  const onSearch = useCallback(
    (formValues: FilterFormValues) => {
      setActiveFilters(formValues);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setRowSelectionModel([]);
      pushUrlParams(formValues, 0, paginationModel.pageSize);
    },
    [paginationModel.pageSize, pushUrlParams],
  );

  const handlePaginationModelChange = useCallback(
    (newModel: GridPaginationModel) => {
      setPaginationModel(newModel);
      setRowSelectionModel([]);
      pushUrlParams(activeFilters, newModel.page, newModel.pageSize);
    },
    [activeFilters, pushUrlParams],
  );

  const selectedIds = rowSelectionModel as Array<string>;
  const selectionExceedsLimit = selectedIds.length > MAX_SELECTION;
  const actionDisabled = selectedIds.length === 0 || selectionExceedsLimit;

  const handleRetryConfirm = useCallback(() => {
    const ids = rowSelectionModel as Array<string>;
    retryMutation.mutate(
      { customerId: activeFilters.customerId, ids },
      {
        onSuccess: (result) => {
          enqueueSnackbar(`${result.result.retriedCount} failure log(s) queued for retry`, { variant: 'success' });
          setRowSelectionModel([]);
          setRetryDialogOpen(false);
        },
        onError: (err) => {
          enqueueSnackbar(`Error retrying failures: ${err.message}`, { variant: 'error' });
          setRetryDialogOpen(false);
        },
      },
    );
  }, [retryMutation, activeFilters.customerId, rowSelectionModel, enqueueSnackbar]);

  const handleDeleteConfirm = useCallback(() => {
    const ids = rowSelectionModel as Array<string>;
    deleteMutation.mutate(
      { customerId: activeFilters.customerId, ids },
      {
        onSuccess: (result) => {
          enqueueSnackbar(`${result.result.deletedCount} failure log(s) deleted`, { variant: 'success' });
          setRowSelectionModel([]);
          setDeleteDialogOpen(false);
        },
        onError: (err) => {
          enqueueSnackbar(`Error deleting failure logs: ${err.message}`, { variant: 'error' });
          setDeleteDialogOpen(false);
        },
      },
    );
  }, [deleteMutation, activeFilters.customerId, rowSelectionModel, enqueueSnackbar]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Hotel Bid Failure Log</title>
      </Helmet>

      <PageSubheader title="Hotel Bid Failure Log" />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" component="form" onSubmit={handleSubmit(onSearch)}>
          <Grid>
            <TextField
              label="Customer ID"
              variant="outlined"
              required
              {...register('customerId')}
              sx={{ width: 250 }}
            />
          </Grid>
          <Grid>
            <TextField label="Campaign ID" variant="outlined" {...register('campaignId')} sx={{ width: 220 }} />
          </Grid>
          <Grid>
            <TextField label="Hotel ID" variant="outlined" {...register('hotelId')} sx={{ width: 260 }} />
          </Grid>
          <Grid>
            <Controller
              name="action"
              control={control}
              render={({ field }) => (
                <FormControl sx={{ width: 180 }}>
                  <InputLabel id="action-filter-label">Action</InputLabel>
                  <Select {...field} labelId="action-filter-label" label="Action">
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="CREATE">CREATE</MenuItem>
                    <MenuItem value="UPDATE">UPDATE</MenuItem>
                    <MenuItem value="DELETE">DELETE</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          <Grid>
            <TextField
              label="From"
              type="date"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              {...register('from')}
              sx={{ width: 200 }}
            />
          </Grid>
          <Grid>
            <TextField
              label="To"
              type="date"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              {...register('to')}
              sx={{ width: 200 }}
            />
          </Grid>
          <Grid>
            <Button variant="contained" color="primary" type="submit">
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Box>
        <DataGrid
          checkboxSelection
          disableRowSelectionOnClick
          rows={data?.result ?? []}
          columns={COLUMNS}
          loading={isFetching}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[20, 50, 100]}
          slots={{ pagination: GridPagination }}
          getRowId={(row) => row.id}
          autoHeight
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(model) => setRowSelectionModel(model)}
        />
      </Box>

      <Box mt={2} display="flex" alignItems="center" gap={2}>
        {selectionExceedsLimit && (
          <Typography variant="body2" color="error">
            Selection exceeds maximum of {MAX_SELECTION} rows.
          </Typography>
        )}
        <Button
          variant="contained"
          color="primary"
          disabled={actionDisabled || retryMutation.isPending}
          onClick={() => setRetryDialogOpen(true)}
        >
          Retry Selected ({selectedIds.length})
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={actionDisabled || deleteMutation.isPending}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete Selected ({selectedIds.length})
        </Button>
      </Box>

      <Dialog open={retryDialogOpen} onClose={() => setRetryDialogOpen(false)}>
        <DialogTitle>Confirm Retry</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to retry {selectedIds.length} selected failure log(s)?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setRetryDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleRetryConfirm} disabled={retryMutation.isPending}>
            {retryMutation.isPending ? 'Retrying…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete {selectedIds.length} selected failure log(s)? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} disabled={deleteMutation.isPending}>
            {deleteMutation.isPending ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default HotelBidFailureLog;
